import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Process Pick Ticket BOL Step
 *
 * Processes inbound emails containing filled pick ticket BOL documents:
 * 1. Classifies the email to detect pick ticket BOL attachments
 * 2. Extracts handwritten data from the document (farm, quantities, etc.)
 * 3. Maps extracted quantities to Famous product IDs
 * 4. Generates Famous Estimate Import CSV
 * 5. Optionally uploads to Famous and/or sends email notification
 */
export const processPickTicketBOLStep = defineStepConfig({
  type: "process_pick_ticket_bol",
  name: "Process Pick Ticket BOL",
  description:
    "Extract handwritten data from pick ticket BOL and generate Famous estimate import",
  category: "Integration",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email containing the pick ticket BOL attachment",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    success: z.boolean().describe("Whether processing was successful"),
    isPickTicketBOL: z
      .boolean()
      .describe("Whether the email was classified as a pick ticket BOL"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Classification confidence score"),
    transferNumber: z
      .string()
      .nullish()
      .describe("Extracted transfer number from the BOL"),
    farm: z
      .string()
      .nullish()
      .describe(
        "Extracted farm name (GL Farm, Amestoy Farm, Smith Sundy Farm)",
      ),
    destination: z.string().nullish().describe("Extracted destination"),
    detailCount: z
      .number()
      .describe("Number of line items in the generated import"),
    csvContent: z
      .string()
      .nullish()
      .describe("The generated CSV content (if successful)"),
    uploadedFilename: z
      .string()
      .nullish()
      .describe("Filename uploaded to Famous (if uploaded)"),
    error: z.string().nullish().describe("Error message if processing failed"),
  }),

  configSchema: z.object({
    dryRun: z
      .boolean()
      .optional()
      .describe("If true, skip upload to Famous and just generate CSV"),
    useTestWarehouse: z
      .boolean()
      .optional()
      .describe(
        "If true, use test warehouse (CASE LABEL ESTIMATES) instead of production warehouses",
      ),
    skipLotFields: z
      .boolean()
      .optional()
      .describe(
        "If true, leave Lot ID and Lot Description blank in the import",
      ),
    sendNotificationEmail: z
      .boolean()
      .optional()
      .describe("If true, send email notification with CSV attachment"),
    notificationEmailTo: z
      .string()
      .optional()
      .describe("Email address to send notification to"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
